import React, { useEffect, useState } from "react";
import "./App.css";
import Login from "./pages/login";
import SAMLLogin from "./pages/samlLogin";
import SAMLError from "./pages/samlError";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Dashboard from "./pages/dashboard";
import ProtectedRoute from "./services/Auth";
import Prenotations from "./pages/prenotations";
import EditAgenda from "./pages/editAgenda";
import PharmacyList from "./pages/pharmacyList";
import { useSelector } from "react-redux";
import axios from "axios";
import { useAppDispatch } from "./redux";
import { deleteUser } from "./redux/reducer/LoginReducer";
import AlertMessageModal from "./components/alertMessageModal";
import { t } from "i18next";
import ManualDownload from "./components/common/manualDownload";
import Statistics from "./pages/statistics";
import TagManager from "react-gtm-module";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import Enrollment from "./pages/enrollment";
import CitizenForm from "./pages/citizenForm";
import CitizenDetail from "./pages/citizenDetail";
import CitizenSurveyDetail from "./pages/citizenSurveyDetail";
import PageNotFound from "./pages/PageNotFound";
import SurveyForm from "./pages/survey/surveyForm";
import SurveyReport from "./pages/survey/reports/surveyReport";
import FarmaciaSurveyReport from "./pages/survey/reports/farmaciaSurveyReport";
import FarmaciaDetail from "./pages/survey/reports/farmaciaDetail";
import CookiePrivacy from "./components/CookiesPrivacy";
import Metadata from "./components/metadata";
import ChangePassword from "./pages/ChangePassword";
import TelemedicinePharmacy from "./pages/telemedicinePharmacy";
import TelemedicineDoctor from "./pages/telemedicineDoctor";
import TelemedicineMeeting from "./pages/telemedicineMeeting";
const queryClient = new QueryClient();

function App() {
  let { tagManagerBackoffice } = useSelector((state: any) => state.login);
  const [showResetContentModal, setShowResetContentModal] = useState(false);
  const dispatch = useAppDispatch();

  let routesList = [
    { path: "/telemedicine/pharmacy/:eventUuid?", element: <TelemedicinePharmacy /> },
    { path: "/telemedicine/doctor/:doctorToken", element: <TelemedicineDoctor /> },
    { path: "/telemedicine/meeting/:meetingToken", element: <TelemedicineMeeting /> },
    { path: "/", element: <SAMLLogin /> },
    { path: "/login", element: <Login /> },
    { path: "/change-password", element: <ChangePassword /> },
    { path: "/saml", element: <SAMLLogin /> },
    { path: "/saml/error", element: <SAMLError /> },
    {
      path: "/dashboard",
      element: (
        <>
          <ProtectedRoute />
          <Dashboard />
          <ManualDownload />
        </>
      ),
    },
    {
      path: "/prenotation",
      element: (
        <>
          <ProtectedRoute />
          <Prenotations />
          <ManualDownload />
        </>
      ),
    },
    {
      path: "/enrollment",
      element: (
        <>
          <ProtectedRoute />
          <Enrollment />
          <ManualDownload />
        </>
      ),
    },
    {
      path: "/citizen-form/:id?",
      element: (
        <>
          <ProtectedRoute />
          <CitizenForm />
          <ManualDownload />
        </>
      ),
    },
    {
      path: "/enrollment/citizen-detail/:id",
      element: (
        <>
          <ProtectedRoute />
          <CitizenDetail />
          <ManualDownload />
        </>
      ),
    },
    {
      path: "/survey-form/:id",
      element: (
        <>
          <ProtectedRoute />
          <SurveyForm />
          <ManualDownload />
        </>
      ),
    },
    {
      path: "/enrollment/survey-detail/:id",
      element: (
        <>
          <ProtectedRoute />
          <CitizenSurveyDetail />
        </>
      ),
    },
    {
      path: "/surveys",
      element: (
        <>
          <ProtectedRoute />
          <SurveyReport />
          <ManualDownload />
        </>
      ),
    },
    {
      path: "/surveys/farmacia/report",
      element: (
        <>
          <ProtectedRoute />
          <FarmaciaSurveyReport />
          <ManualDownload />
        </>
      ),
    },
    {
      path: "/surveys/farmacia/view",
      element: (
        <>
          <ProtectedRoute />
          <FarmaciaDetail />
          <ManualDownload />
        </>
      ),
    },
    {
      path: "/editagenda",
      element: (
        <>
          <ProtectedRoute />
          <EditAgenda />
          <ManualDownload />
        </>
      ),
    },
    {
      path: "/pharmacyList",
      element: (
        <>
          <ProtectedRoute />
          <PharmacyList />
          <ManualDownload />
        </>
      ),
    },
    {
      path: "/statistics",
      element: (
        <>
          <ProtectedRoute />
          <Statistics />
          <ManualDownload />
        </>
      ),
    },
    {
      path: "*",
      element: (
        <>
          <PageNotFound />
        </>
      ),
    },
  ];

  const pageReload = () => {
    window.location.href = "/";
  };

  useEffect(() => {
    if (tagManagerBackoffice) {
      const tagManagerArgs = {
        gtmId: tagManagerBackoffice,
      };
      TagManager.initialize(tagManagerArgs);
    }
  }, []);

  /**
   * Intercept any unauthorized request.
   * Dispatch logout action accordingly.
   */
  const UNAUTHORIZED = 401;
  const RESET_CONTENT = 205;
  axios.interceptors.response.use(
    (response) => {
      if (response.status === RESET_CONTENT) {
        setShowResetContentModal(true);
      } else {
        return response;
      }
    },
    (error) => {
      const { status } = error.response;
      if (status === UNAUTHORIZED) {
        dispatch(deleteUser());
      }
      return Promise.reject(error);
    }
  );

  return (
    <>
      <QueryClientProvider client={queryClient}>
        <Metadata />
        <BrowserRouter>
          <Routes>
            {routesList.map((route: any, key: number) => (
              <Route
                key={key}
                path={route.path}
                element={route.element}
              ></Route>
            ))}
          </Routes>
        </BrowserRouter>
      </QueryClientProvider>
      <CookiePrivacy />
      <AlertMessageModal
        title={t("attention")}
        description={t("popup-reset-content.message")}
        titleButtonPrimary={t("popup-reset-content.button-reload")}
        handleClosePrimary={pageReload}
        show={showResetContentModal}
      />
    </>
  );
}

export default App;
